"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { getSupabaseBrowserClient } from "@/lib/supabase";

type AdminRow = { role: string };

export function AuthNavButton() {
  const supabase = getSupabaseBrowserClient();
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    async function loadAdmin(userId: string) {
      try {
        const { data } = await supabase.from("admin_users").select("role").eq("user_id", userId).maybeSingle<AdminRow>();
        setIsAdmin(Boolean(data));
      } catch {
        setIsAdmin(false);
      }
    }

    async function load() {
      try {
        const { data } = await supabase.auth.getUser();
        setEmail(data.user?.email ?? null);
        if (data.user) await loadAdmin(data.user.id);
      } catch {
        setEmail(null);
        setIsAdmin(false);
      }
    }

    void load();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setEmail(session?.user.email ?? null);
      setOpen(false);
      if (session?.user) void loadAdmin(session.user.id); else setIsAdmin(false);
    });

    return () => listener.subscription.unsubscribe();
  }, [supabase]);

  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    setEmail(null);
    setIsAdmin(false);
    setOpen(false);
  }

  if (!email) {
    return <Link href={"/account?returnTo=" + encodeURIComponent(pathname || "/")} className="hidden rounded-md border border-[#CBD5E1] px-3 py-2 text-sm font-semibold text-[#111827] hover:border-[#F68A00] sm:inline-flex">Login</Link>;
  }

  return (
    <div ref={menuRef} className="relative">
      <button type="button" aria-label="Account menu" aria-expanded={open} title={email} onClick={() => setOpen((current) => !current)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#F68A00] bg-white text-[#111827] hover:bg-orange-50">
        <span className="relative block h-5 w-5" aria-hidden="true">
          <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 rounded-full bg-[#F68A00]" />
          <span className="absolute bottom-0 left-1/2 h-3 w-4 -translate-x-1/2 rounded-t-full bg-[#F68A00]" />
        </span>
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 px-4 py-3 text-xs text-[#64748B]">
            <p className="truncate font-semibold text-[#111827]">{email}</p>
          </div>
          <div className="grid p-1 text-sm font-semibold text-[#334155]">
            <MenuLink href="/account" label="Customer account" setOpen={setOpen} />
            <MenuLink href="/orders" label="Order history" setOpen={setOpen} />
            <MenuLink href="/community/my-posts" label="My forum posts" setOpen={setOpen} />
            {isAdmin ? <MenuLink href="/admin" label="Admin dashboard" setOpen={setOpen} /> : null}
            <button type="button" onClick={signOut} className="rounded-md px-3 py-2 text-left hover:bg-orange-50 hover:text-[#F68A00]">Logout</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuLink({ href, label, setOpen }: { href: string; label: string; setOpen: (open: boolean) => void }) {
  return <Link href={href} onClick={() => setOpen(false)} className="rounded-md px-3 py-2 hover:bg-orange-50 hover:text-[#F68A00]">{label}</Link>;
}
